import { Check, Circle, Clock, Loader2, Flame } from "lucide-react";

const focusTarget = (target) => {
  const el = document.querySelector(`[data-testid="${target}"]`);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
};

export const ThisWeekCard = ({ plan, onDone, busy }) => {
  const items = plan?.this_week || [];
  const done = plan?.done_this_week || [];
  const goal = plan?.weekly_goal || 0;
  const touches = plan?.touches_this_week || 0;
  const streak = plan?.streak || { current: 0, best: 0 };
  const pct = goal ? Math.min(100, Math.round((touches * 100) / goal)) : 0;

  return (
    <div className="rounded-3xl bg-white border border-linen-border shadow-soft p-6" data-testid="this-week-card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-[11px] uppercase tracking-widest text-teal font-bold">This week</p>
          <h3 className="mt-1 font-heading font-medium text-xl text-navy">
            {goal} conversations is the target
          </h3>
          <p className="mt-1 text-sm text-ink-500">
            {touches === 0
              ? "Nothing started yet this week — the first one is the hard one."
              : `${touches} of ${goal} done. ${touches >= goal ? "That's the week made." : "Keep going."}`}
          </p>
        </div>
        <span className="text-sm text-ink-500 tabular-nums shrink-0" data-testid="week-progress">
          {touches}/{goal}
        </span>
      </div>

      <div className="mt-4 h-2 rounded-full bg-linen overflow-hidden">
        <div
          className="h-full rounded-full bg-teal"
          style={{ width: `${pct}%`, transition: "width .6s cubic-bezier(.22,1,.36,1)" }}
        />
      </div>

      {(streak.current > 0 || streak.best > 0) && (
        <div
          className="mt-4 flex flex-wrap items-center gap-2.5 rounded-2xl bg-lime/15 border border-lime/40 px-4 py-3"
          data-testid="streak-block"
        >
          <Flame size={16} className="text-navy shrink-0" />
          <p className="text-sm text-navy font-semibold">
            {streak.current > 0
              ? `${streak.current} week${streak.current === 1 ? "" : "s"} in a row`
              : "Streak broken — start a new one"}
          </p>
          <span className="text-[11px] text-ink-500">
            {streak.current > 0 && streak.current >= streak.best
              ? "That's your best run yet. Don't break it."
              : `Best run: ${streak.best} week${streak.best === 1 ? "" : "s"}`}
          </span>
        </div>
      )}

      <ul className="mt-5 space-y-2.5" data-testid="this-week-items">
        {items.map((a) => (
          <li
            key={a.key}
            data-testid={`week-item-${a.key}`}
            className="flex items-start gap-3 rounded-2xl border border-linen-border p-3.5"
          >
            <button
              onClick={() => onDone(a, { log: Boolean(a.contact) })}
              disabled={busy}
              data-testid={`week-done-${a.key}`}
              aria-label={`Mark ${a.title} done`}
              className="shrink-0 mt-0.5 w-6 h-6 rounded-full border border-ink-300 text-ink-300 flex items-center justify-center hover:border-teal hover:text-teal transition-colors disabled:opacity-50"
            >
              {busy ? <Loader2 size={11} className="animate-spin" /> : <Circle size={7} fill="currentColor" />}
            </button>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-navy">{a.title}</p>
              <p className="mt-0.5 text-[12.5px] text-ink-500 leading-relaxed">{a.why}</p>
              <div className="mt-1.5 flex items-center gap-3">
                <span className="inline-flex items-center gap-1 text-[11px] text-ink-400">
                  <Clock size={11} /> {a.minutes} min
                </span>
                {a.cta?.type === "focus" && (
                  <button
                    onClick={() => focusTarget(a.cta.target)}
                    data-testid={`week-focus-${a.key}`}
                    className="text-[11px] font-semibold text-teal hover:underline"
                  >
                    {a.cta.label}
                  </button>
                )}
                {a.cta?.type === "link" && (
                  <a href={a.cta.path} className="text-[11px] font-semibold text-teal hover:underline">
                    {a.cta.label}
                  </a>
                )}
              </div>
            </div>
          </li>
        ))}

        {items.length === 0 && (
          <li className="rounded-2xl bg-linen/60 border border-linen-border p-4 text-sm text-ink-500">
            Nothing else queued. Add a few more names and this fills back up.
          </li>
        )}

        {done.map((a) => (
          <li
            key={a.key}
            data-testid={`week-done-item-${a.key}`}
            className="flex items-center gap-3 rounded-2xl border border-teal/30 bg-teal-light/15 p-3.5"
          >
            <span className="shrink-0 w-6 h-6 rounded-full bg-teal text-white flex items-center justify-center">
              <Check size={13} strokeWidth={3} />
            </span>
            <p className="text-sm text-ink-400 line-through">{a.title}</p>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default ThisWeekCard;
